import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtINR, fmtDate, today } from "@/lib/format";
import { Plus, Trash2, RefreshCw, Tag, Calendar, AlertCircle } from "lucide-react";

/**
 * Iter-151 · Phase-1 · Dealership 360 Price List module.
 *
 * Central price catalogue for Vehicles (ex-showroom), Accessories (list price)
 * and Extended Warranty plans. Each row is time-bounded — creating a new row
 * for the same item auto-expires the older row's `effective_to` to the
 * previous day. Deactivated rows stay for historical reference.
 *
 * Consumers: Quotation engine reads via `dealer_price_list_service.get_active_price`.
 */
const TYPE_LABELS = {
  vehicle: "🚗 Vehicle",
  accessory: "🔧 Accessory",
  extended_warranty: "🛡 Extended Warranty",
};

export default function PriceListsTab() {
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    const [it, pl] = await Promise.all([
      api.get("/items", { params: {} }),
      api.get("/dealership/price-lists", { params: { item_type: typeFilter || undefined, active_only: activeOnly } }),
    ]);
    // Only surface price-listable item types
    const priceable = it.data.filter((x) => ["vehicle","accessory","extended_warranty"].includes(x.item_type));
    setItems(priceable);
    setRows(pl.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeFilter, activeOnly]);

  const priceableItems = useMemo(() => items.filter((x) =>
    !form.item_type || form.item_type === x.item_type
  ), [items, form.item_type]);

  const save = async () => {
    if (!form.item_id) return toast.error("Pick an item first");
    if (!form.ex_showroom_price || form.ex_showroom_price <= 0) return toast.error("Ex-showroom price must be > 0");
    try {
      await api.post("/dealership/price-lists", form);
      toast.success("Price entry added — item sale_price synced");
      setAddOpen(false); setForm(emptyForm()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const update = async () => {
    if (!editRow) return;
    try {
      await api.patch(`/dealership/price-lists/${editRow.id}`, {
        ex_showroom_price: editRow.ex_showroom_price,
        cost_price: editRow.cost_price,
        list_price: editRow.list_price,
        on_road_estimate: editRow.on_road_estimate,
        notes: editRow.notes,
        effective_from: editRow.effective_from,
        effective_to: editRow.effective_to,
        active: editRow.active,
      });
      toast.success("Updated");
      setEditRow(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete price entry for ${row.item_name}?`)) return;
    try {
      await api.delete(`/dealership/price-lists/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="price-lists-tab">
      {/* Filters + Add */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Price List · Ex-showroom · Accessories · Extended Warranty</div>
          <div className="text-[11px] text-muted-foreground">
            Central catalogue driving the Quotation engine. Adding a new row auto-expires the older one for the same item.
          </div>
        </div>
        <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-sm h-8 w-56" data-testid="pl-type-filter">
            <SelectValue placeholder="All item types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All item types</SelectItem>
            {Object.entries(TYPE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1.5 text-[11px] font-mono uppercase cursor-pointer">
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} data-testid="pl-active-only" />
          Active only
        </label>
        <Button size="sm" variant="ghost" onClick={load} className="h-8 gap-1" title="Refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
        {/* Iter-152 · Bulk CSV upload + template download */}
        <Button size="sm" variant="outline" onClick={async () => {
          try {
            const r = await api.get("/dealership/price-lists/csv-template", { responseType: "blob" });
            const blob = new Blob([r.data], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = "price_list_template.csv";
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
          } catch (e) { toast.error("Failed"); }
        }} className="rounded-sm h-8 gap-1 text-[10px]" data-testid="pl-template-btn">
          Template
        </Button>
        <label className="rounded-sm h-8 gap-1 text-[10px] px-3 border border-input inline-flex items-center cursor-pointer bg-background hover:bg-muted/30" data-testid="pl-bulk-label">
          📥 Bulk CSV
          <input type="file" accept=".csv" className="hidden"
            data-testid="pl-bulk-input"
            onChange={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              const fd = new FormData(); fd.append("file", f);
              try {
                const r = await api.post("/dealership/price-lists/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                const s = r.data;
                toast.success(`Uploaded: ${s.prices_created} price rows · ${s.items_created} new items · ${s.items_reused} reused${s.skipped ? ` · ${s.skipped} skipped` : ""}`, { duration: 8000 });
                if (s.errors?.length) toast.warning(`${s.errors.length} row(s) had issues — check console`, { duration: 6000 });
                if (s.errors?.length) console.warn("Bulk price-list errors:", s.errors);
                load();
              } catch (er) { toast.error(er?.response?.data?.detail || "Upload failed"); }
              e.target.value = "";
            }}
          />
        </label>
        <Button onClick={() => { setForm(emptyForm()); setAddOpen(true); }} className="rounded-sm h-8 bg-accent hover:bg-accent/90 text-white gap-1" data-testid="pl-add-btn">
          <Plus className="w-3.5 h-3.5" /> Add Price
        </Button>
      </div>

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead>
            <tr>
              <th>Type</th><th>Item</th><th>Variant / Body</th>
              <th className="text-right">Ex-showroom</th>
              <th className="text-right">Cost</th>
              <th className="text-right">List</th>
              <th className="text-right">On-road est.</th>
              <th>Effective</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="10" className="text-center py-10 text-muted-foreground">No price entries yet. Add your first vehicle / accessory / extended-warranty price.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`pl-row-${r.item_sku || r.item_id.slice(0,8)}`} className={r.active ? "" : "opacity-50"}>
                <td className="text-[10px] font-mono uppercase text-accent">{TYPE_LABELS[r.item_type] || r.item_type}</td>
                <td className="text-xs">
                  <div className="font-medium">{r.item_name}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">{r.item_sku}</div>
                </td>
                <td className="text-[10px] text-muted-foreground">
                  {r.variant && <div>{r.variant}</div>}
                  {r.body_type && <div>{r.body_type}</div>}
                </td>
                <td className="text-right tabular-nums font-medium">{fmtINR(r.ex_showroom_price)}</td>
                <td className="text-right tabular-nums text-xs text-muted-foreground">{r.cost_price ? fmtINR(r.cost_price) : "—"}</td>
                <td className="text-right tabular-nums text-xs">{r.list_price ? fmtINR(r.list_price) : "—"}</td>
                <td className="text-right tabular-nums text-xs">{r.on_road_estimate ? fmtINR(r.on_road_estimate) : "—"}</td>
                <td className="text-[10px]">
                  <div>{fmtDate(r.effective_from)}</div>
                  <div className="text-muted-foreground">→ {r.effective_to ? fmtDate(r.effective_to) : <span className="font-mono">ongoing</span>}</div>
                </td>
                <td>
                  <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-sm ${r.active ? "bg-emerald-500/10 text-emerald-600" : "bg-muted text-muted-foreground"}`}>
                    {r.active ? "Active" : "Expired"}
                  </span>
                </td>
                <td className="text-right">
                  <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setEditRow({ ...r })}>Edit</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-[10px] text-red-500" onClick={() => remove(r)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add sheet */}
      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl rounded-none space-y-3 overflow-y-auto" data-testid="pl-add-sheet">
          <SheetHeader>
            <SheetTitle className="font-heading flex items-center gap-2"><Tag className="w-4 h-4 text-accent" /> New Price Entry</SheetTitle>
            <SheetDescription>Adding a price auto-expires the older active row for the same item. Item's sale_price is kept in sync.</SheetDescription>
          </SheetHeader>

          <div className="space-y-3">
            <div>
              <Label>Item type</Label>
              <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v, item_id: "" })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="pl-type-select"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent>{Object.entries(TYPE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <div className="text-[10px] text-muted-foreground mt-1">
                Don't see your item? Add it in <b>Inventory → Items</b> with the matching type first.
              </div>
            </div>
            <div>
              <Label>Item *</Label>
              <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="pl-item-select"><SelectValue placeholder={form.item_type ? "Pick item" : "Pick a type first"} /></SelectTrigger>
                <SelectContent>
                  {priceableItems.length === 0
                    ? <div className="px-2 py-2 text-[11px] text-muted-foreground">No items — create in Inventory first</div>
                    : priceableItems.map((it) => <SelectItem key={it.id} value={it.id}>{it.sku} · {it.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ex-showroom ₹ *</Label>
                <Input type="number" value={form.ex_showroom_price || ""} onChange={(e) => setForm({ ...form, ex_showroom_price: Number(e.target.value) })}
                  className="rounded-sm mt-1 font-mono text-right" data-testid="pl-ex" />
              </div>
              <div><Label>Cost ₹ (dealer margin)</Label>
                <Input type="number" value={form.cost_price || ""} onChange={(e) => setForm({ ...form, cost_price: Number(e.target.value) })}
                  className="rounded-sm mt-1 font-mono text-right" />
              </div>
              <div><Label>List price ₹ (MRP)</Label>
                <Input type="number" value={form.list_price || ""} onChange={(e) => setForm({ ...form, list_price: Number(e.target.value) })}
                  className="rounded-sm mt-1 font-mono text-right" placeholder="Same as ex-showroom" />
              </div>
              <div><Label>On-road estimate ₹</Label>
                <Input type="number" value={form.on_road_estimate || ""} onChange={(e) => setForm({ ...form, on_road_estimate: Number(e.target.value) })}
                  className="rounded-sm mt-1 font-mono text-right" placeholder="After RTO, insurance…" />
              </div>
              <div><Label>Effective from</Label>
                <Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                  className="rounded-sm mt-1" data-testid="pl-from" />
              </div>
              <div><Label>Effective to</Label>
                <Input type="date" value={form.effective_to || ""} onChange={(e) => setForm({ ...form, effective_to: e.target.value || null })}
                  className="rounded-sm mt-1" placeholder="Ongoing" />
              </div>
              <div className="col-span-2"><Label>Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="rounded-sm mt-1" placeholder="Optional — e.g. FY26 revised price, festive discount season" />
              </div>
            </div>
          </div>
          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="pl-save">Save price entry</Button>
        </SheetContent>
      </Sheet>

      {/* Edit sheet */}
      <Sheet open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader>
            <SheetTitle className="font-heading">Edit Price Entry</SheetTitle>
            <SheetDescription>Editing the ex-showroom price also syncs the item's `sale_price` field.</SheetDescription>
          </SheetHeader>
          {editRow && (
            <div className="space-y-3">
              <div className="border rounded-sm p-2 bg-muted/20 text-xs">
                <b>{editRow.item_name}</b> <span className="font-mono text-[10px] text-muted-foreground">· {editRow.item_sku}</span>
              </div>
              <div><Label>Ex-showroom ₹</Label>
                <Input type="number" value={editRow.ex_showroom_price} onChange={(e) => setEditRow({ ...editRow, ex_showroom_price: Number(e.target.value) })} className="rounded-sm mt-1 font-mono text-right" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Cost ₹</Label><Input type="number" value={editRow.cost_price || 0} onChange={(e) => setEditRow({ ...editRow, cost_price: Number(e.target.value) })} className="rounded-sm mt-1 font-mono text-right" /></div>
                <div><Label>List ₹</Label><Input type="number" value={editRow.list_price || 0} onChange={(e) => setEditRow({ ...editRow, list_price: Number(e.target.value) })} className="rounded-sm mt-1 font-mono text-right" /></div>
                <div><Label>Effective from</Label><Input type="date" value={editRow.effective_from} onChange={(e) => setEditRow({ ...editRow, effective_from: e.target.value })} className="rounded-sm mt-1" /></div>
                <div><Label>Effective to</Label><Input type="date" value={editRow.effective_to || ""} onChange={(e) => setEditRow({ ...editRow, effective_to: e.target.value || null })} className="rounded-sm mt-1" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={editRow.active} onChange={(e) => setEditRow({ ...editRow, active: e.target.checked })} />
                Active
              </label>
              <Button onClick={update} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white">Save</Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function emptyForm() {
  return {
    item_type: "vehicle",
    item_id: "",
    ex_showroom_price: 0, cost_price: 0, list_price: 0, on_road_estimate: 0,
    effective_from: today(),
    effective_to: null,
    notes: "",
  };
}
